import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { MDText } from 'i18n-react';

import toString from 'lodash/toString';
import get from 'lodash/get';
import noop from 'lodash/noop';

import IconButton from '@adsk/bim360-matrix-react-icon-button';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import ActionButton from '@adsk/bim360-matrix-react-action-button';
import Loader from '@adsk/bim360-matrix-react-loader';

import AutoResizer from './AutoResizer';
import GridTable from './GridTable';
import TableHeader from './TableHeader';
import TableRow from './TableRow';
import TableCell from './TableCell';
import SelectionBox from './SelectionBox';
import ExpandIcon from './ExpandIcon';
import ColumnResizer from './ColumnResizer';
import Column from './Column';
import ColumnManager from '@adsk/bim360-matrix-react-helpers/es/ColumnManager';
import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';
import { deprecateComponent } from '@adsk/bim360-matrix-react-helpers/es/deprecate';

import {
  normalizeColumns,
  callOrReturn,
  arrayHasSameValues,
  arrayAddValue,
  arrayRemoveValue
} from '@adsk/bim360-matrix-react-helpers/es/tableHelper';

import settingsIcon from '@adsk/bim360-matrix-react-assets/settings.svg';
import tableArrow from '@adsk/bim360-matrix-react-assets/table-arrow.svg';
import emptyBox from '@adsk/bim360-matrix-react-assets/empty-box.svg';
import './_BaseTable.scss';

/**
 * Sort order for the table
 */
export const SortOrder = {
  /**
   * Sort items in ascending order
   */
  ASC: 'ASC',
  /**
   * Sort items in descending order
   */
  DESC: 'DESC'
};

// width of selectionColumn and actionColumn,
// 16 - icon width, 15 - one side padding, 7.5 - another side padding, to make the gutter width be 15
// should override it via custom props if you want to change the gutter width
const DEFAULT_COLUMN_WIDTH = 16 + 15 + 7.5;

/**
 * New Table component based on react-virtualized
 */
export class BaseTable extends React.Component {
  constructor(props) {
    super(props);
    deprecateComponent('BaseTable', 'MatrixTable');

    this.state = {
      // used for auto height table
      tableHeight: props.headerHeight + props.footerHeight,
      scrollbarWidth: 0,
      horizontalScrollbarWidth: 0,
      selectedRowKeys: [...(props.selectedRowKeys || props.defaultSelectedRowKeys)],
      expandedRowKeys: [...(props.expandedRowKeys || props.defaultExpandedRowKeys)],
      resizingKey: null,
      hoverRowKey: null
    };
    this.columnManager = new ColumnManager(this.normalizeColumns(props), props.fixed);

    this._rowHeight = this._rowHeight.bind(this);
    this._setContainerRef = this._setContainerRef.bind(this);
    this._getContainerRef = this._getContainerRef.bind(this);
    this._setMainTableRef = this._setMainTableRef.bind(this);
    this._setLeftTableRef = this._setLeftTableRef.bind(this);
    this._setRightTableRef = this._setRightTableRef.bind(this);

    this.renderRow = this.renderRow.bind(this);
    this.renderRowCell = this.renderRowCell.bind(this);
    this.renderHeader = this.renderHeader.bind(this);
    this.renderHeaderCell = this.renderHeaderCell.bind(this);
    this.renderDefaultEmptyLayer = this.renderDefaultEmptyLayer.bind(this);

    this._handleScrollbarPresenceChange = this._handleScrollbarPresenceChange.bind(this);
    this._handleResize = this._handleResize.bind(this);
    this._handleScroll = this._handleScroll.bind(this);
    this._handleVerticalScroll = this._handleVerticalScroll.bind(this);
    this._handleRowsRendered = this._handleRowsRendered.bind(this);
    this._handleColumnResize = this._handleColumnResize.bind(this);
    this._handleColumnResizeStart = this._handleColumnResizeStart.bind(this);
    this._handleColumnResizeStop = this._handleColumnResizeStop.bind(this);
    this._handleColumnSort = this._handleColumnSort.bind(this);
    this._handleRowHover = this._handleRowHover.bind(this);

    this._size = {};
    this._scroll = {};
    this._scrollHeight = 0;
    this._lastScanedRowIndex = -1;
    this._hasDataChangedSinceEndReached = true;
  }

  normalizeColumns(props) {
    const columns = props.columns ? [...props.columns] : normalizeColumns(props.children);
    this.insertSelectionColumn(columns, props);
    this.insertActionColumn(columns, props);
    return columns;
  }

  getAllRowKeys(data) {
    if (!data) return [];

    return data.reduce((keys, rowData) => {
      keys.push(rowData[this.props.rowKey]);
      const childrenKeys = this.getAllRowKeys(rowData.children);
      if (childrenKeys.length) keys = keys.concat(childrenKeys);
      return keys;
    }, []);
  }

  getExpandableRowKeys(data) {
    if (!data) return [];

    return data.reduce((keys, rowData) => {
      if (!Array.isArray(rowData.children)) return keys;
      keys.push(rowData[this.props.rowKey]);
      const childrenKeys = this.getExpandableRowKeys(rowData.children);
      if (childrenKeys.length) keys = keys.concat(childrenKeys);
      return keys;
    }, []);
  }

  getSelectableRowKeys(data, selectable) {
    if (!data || !selectable) return [];

    return data.reduce((keys, rowData, rowIndex) => {
      if (callOrReturn(selectable, { rowData, rowIndex })) {
        keys.push(rowData[this.props.rowKey]);
      }
      // if the row is expandable, get the children's keys recursively
      const childrenKeys = this.getSelectableRowKeys(rowData.children, selectable);
      if (childrenKeys.length) keys = keys.concat(childrenKeys);
      return keys;
    }, []);
  }

  insertSelectionColumn(columns, props) {
    if (!props.selectable) return;

    const { rowKey } = props;

    const selectableRowKeys = this.getSelectableRowKeys(props.data, props.selectable);
    columns.unshift({
      key: '__selection__',
      width: DEFAULT_COLUMN_WIDTH,
      flexShrink: 0,
      resizable: false,
      frozen: 'left',
      renderHeader: () => {
        const { selectedRowKeys } = this.state;
        const checked = selectedRowKeys.length > 0;
        const partialChecked = checked && selectedRowKeys.length < selectableRowKeys.length;
        return (
          <SelectionBox
            checked={checked}
            indeterminate={partialChecked}
            onChange={() => {
              const needClear = checked && !partialChecked;
              const selectedRowKeys = needClear ? [] : [...selectableRowKeys];
              // if `selectedRowKeys` is uncontrolled, update internal state
              if (!this.props.selectedRowKeys) {
                this.setState({ selectedRowKeys });
                this.forceUpdateTable();
              }
              this.forceUpdateTable();
              props.onSelectedRowsChange(selectedRowKeys);
              props.onSelectAll(!needClear, selectedRowKeys);
            }}
          />
        );
      },
      renderCell: ({ rowData, rowIndex }) => {
        if (!selectableRowKeys.includes(rowData[rowKey])) return null;

        const { selectedRowKeys } = this.state;
        return (
          <SelectionBox
            checked={selectedRowKeys.includes(rowData[rowKey])}
            onChange={selected => {
              const selectedRowKeys = [...this.state.selectedRowKeys];
              if (selected) {
                arrayAddValue(selectedRowKeys, rowData[rowKey]);
              } else {
                arrayRemoveValue(selectedRowKeys, rowData[rowKey]);
              }
              // if `selectedRowKeys` is uncontrolled, update internal state
              if (!this.props.selectedRowKeys) {
                this.setState({ selectedRowKeys });
                this.forceUpdateTable();
              }
              props.onRowSelect({ selected, rowData, rowIndex });
              props.onSelectedRowsChange(selectedRowKeys);
            }}
          />
        );
      },
      ...props.selectionColumnProps
    });
  }

  insertActionColumn(columns, props) {
    if (!props.onSettingsClick && !props.actionOptions) return;

    columns.push({
      key: '__action__',
      width: DEFAULT_COLUMN_WIDTH,
      flexGrow: 1,
      flexShrink: 0,
      align: 'right',
      resizable: false,
      frozen: 'right',
      renderHeader: () =>
        !!props.onSettingsClick && (
          <IconButton className="BaseTable__settings-icon" onClick={props.onSettingsClick}>
            <SvgIcon>{settingsIcon}</SvgIcon>
          </IconButton>
        ),
      renderCell: ({ rowData, rowIndex }) => {
        const { actionOptions } = props;
        if (!actionOptions) return null;
        const options = callOrReturn(actionOptions, { rowData, rowIndex });
        return (
          <ActionButton
            container={this._getContainerRef}
            options={options}
            onSelect={key => {
              props.onRowAction({ key, rowData, rowIndex });
            }}
          />
        );
      },
      ...props.actionColumnProps
    });
  }

  _getExpandedRowCount(rowData) {
    if (rowData.children && this.state.expandedRowKeys.includes(rowData[this.props.rowKey])) {
      const count = rowData.children
        .map(row => this._getExpandedRowCount(row))
        .reduce((total, count) => total + count, 1);
      return count;
    }

    return 1;
  }

  _getFrozenRowCount() {
    return Math.min(this.props.frozenRowCount, this.props.data.length);
  }

  _rowHeight({ index }) {
    return this._getExpandedRowCount(this.props.data[index]) * this.props.rowHeight;
  }

  _setContainerRef(ref) {
    this.container = ref;
    this.props.setContainerRef(ref);
  }

  _getContainerRef() {
    return this.container;
  }

  _setMainTableRef(ref) {
    this.table = ref;
  }

  _setLeftTableRef(ref) {
    this.leftTable = ref;
  }

  _setRightTableRef(ref) {
    this.rightTable = ref;
  }

  //region Table Methods
  forceUpdateTable() {
    this.table && this.table.forceUpdateTable();
    this.leftTable && this.leftTable.forceUpdateTable();
    this.rightTable && this.rightTable.forceUpdateTable();
  }

  getFrozenRowsHeight() {
    return this.table ? this.table.getFrozenRowsHeight() : 0;
  }

  getTotalColumnsWidth() {
    return this.table ? this.table.getTotalColumnsWidth() : 0;
  }

  /**
   * Total height of all rows being measured.
   * The value will be completely estimated initially, as rows are measured, the estimate will be updated.
   * This method is useful to implement auto growable height when adding/removing/expanding rows.
   */
  getTotalRowsHeight() {
    return this.table ? this.table.getTotalRowsHeight() : 0;
  }

  measureAllRows() {
    this.table && this.table.measureAllRows();
    this.leftTable && this.leftTable.measureAllRows();
    this.rightTable && this.rightTable.measureAllRows();
  }

  recomputeRowHeights(startingRowIndex) {
    this.table && this.table.recomputeRowHeights(startingRowIndex);
    this.leftTable && this.leftTable.recomputeRowHeights(startingRowIndex);
    this.rightTable && this.rightTable.recomputeRowHeights(startingRowIndex);
  }

  scrollToPosition(args) {
    this._scroll = args;

    this.table && this.table.scrollToPosition(this._scroll);
    this.leftTable && this.leftTable.scollToTop(args.scrollTop);
    this.rightTable && this.rightTable.scollToTop(args.scrollTop);
  }

  scrollToTop(scrollTop) {
    this._scroll.scrollTop = scrollTop;

    this.table && this.table.scrollToPosition(this._scroll);
    this.leftTable && this.leftTable.scollToTop(scrollTop);
    this.rightTable && this.rightTable.scollToTop(scrollTop);
  }

  scrollToLeft(scrollLeft) {
    this._scroll.scrollLeft = scrollLeft;

    this.table && this.table.scrollToPosition(this._scroll);
  }

  scrollToRow(rowIndex = 0) {
    this.table && this.table.scrollToRow(rowIndex);
    this.leftTable && this.leftTable.scrollToRow(rowIndex);
    this.rightTable && this.rightTable.scrollToRow(rowIndex);
  }

  setSelectedRowKeys(selectedRowKeys) {
    this.setState({
      selectedRowKeys: [...selectedRowKeys]
    });
    this.forceUpdateTable();
  }

  setExpandedRowKeys(expandedRowKeys) {
    this.setState({
      expandedRowKeys: [...expandedRowKeys]
    });
    this.forceUpdateTable();
  }
  //endregion

  //region Renders
  renderRow({ key, columns, rowData, rowIndex, style, depth = 0, rootIndex }) {
    const { rowClassName, rowStyle, renderRow, rowEventHandlers, expandColumnKey } = this.props;

    const rowClass = callOrReturn(rowClassName, { rowData, rowIndex });
    const rowStyleObject = callOrReturn(rowStyle, { rowData, rowIndex });
    const rowKey = rowData[this.props.rowKey];

    const className = cn('BaseTable__row', rowClass, {
      [`BaseTable__row--depth-${depth}`]: !!expandColumnKey,
      'BaseTable__row--expanded': this.state.expandedRowKeys.includes(rowKey),
      'BaseTable__row--selected': this.state.selectedRowKeys.includes(rowKey),
      'BaseTable__row--hovered': rowKey === this.state.hoverRowKey,
      'BaseTable__row--frozen': depth === 0 && rowIndex < this.props.frozenRowCount
    });
    const flattenedStyle = {
      ...style,
      ...rowStyleObject,
      height: this.props.rowHeight,
      overflow: 'hidden'
    };

    const props = {
      key,
      className,
      style: flattenedStyle,
      columns,
      rowIndex,
      rowData,
      rowKey,
      depth,
      rootIndex: rootIndex || rowIndex,
      rowEventHandlers,
      // for fixed table, we need to sync the hover state across the inner tables
      onRowHover: this.columnManager.hasFrozenColumns() ? this._handleRowHover : null,
      renderCell: this.renderRowCell
    };

    const row = renderRow ? renderRow(props) : <TableRow {...props} />;
    if (!expandColumnKey || !Array.isArray(rowData.children) || rowData.children.length === 0)
      return row;

    let expandedRows = null;
    if (!!expandColumnKey && this.state.expandedRowKeys.includes(rowKey)) {
      let top = style.top + this.props.rowHeight;
      expandedRows = rowData.children.map((expandeData, expandIndex) => {
        const expandRow = this.renderRow({
          key: `${key}-${expandIndex}`,
          columns,
          rowData: expandeData,
          rowIndex: expandIndex,
          style: { ...style, top },
          depth: depth + 1,
          rootIndex: rootIndex || rowIndex
        });
        top = top + this._getExpandedRowCount(expandeData) * this.props.rowHeight;
        return expandRow;
      });
    }
    return [row, expandedRows];
  }

  renderRowCell({ column, columnIndex, rowData, rowIndex, depth, rootIndex }) {
    const {
      className,
      dataKey,
      dataGetter,
      tooltip,
      tooltipPopoverProps,
      renderCell,
      key
    } = column;

    const cellData = dataGetter
      ? dataGetter({ column, columnIndex, rowData, rowIndex })
      : get(rowData, dataKey);
    const tip = callOrReturn(tooltip, { cellData, column, columnIndex, rowData, rowIndex });
    const tipProps = callOrReturn(tooltipPopoverProps, {
      cellData,
      column,
      columnIndex,
      rowData,
      rowIndex,
      tooltipContent: tip
    });
    const cell = renderCell ? (
      renderCell({ cellData, column, columnIndex, rowData, rowIndex })
    ) : (
      <TableCell
        className="BaseTable__row-column-text"
        container={this._getContainerRef}
        tooltip={tip}
        tooltipPopoverProps={tipProps}
      >
        {toString(cellData)}
      </TableCell>
    );

    const expandable = Array.isArray(rowData.children) && rowData.children.length > 0;
    const { rowKey } = this.props;

    const cls = cn('BaseTable__row-column', className, {
      'BaseTable__row-column--align-center': column.align === 'center',
      'BaseTable__row-column--align-right': column.align === 'right'
    });
    return (
      <div
        key={`Row${rowIndex}-Col${columnIndex}`}
        data-testid={`BaseTable__row-${rowData[rowKey]}---BaseTable__column-${key}`}
        className={cls}
        style={this.columnManager.getColumnStyle(column.key)}
      >
        {column.key === this.props.expandColumnKey && (
          <ExpandIcon
            depth={depth}
            expandable={expandable}
            expanded={this.state.expandedRowKeys.includes(rowData[rowKey])}
            onExpand={expanded => {
              const expandedRowKeys = [...this.state.expandedRowKeys];
              if (expanded) {
                arrayAddValue(expandedRowKeys, rowData[rowKey]);
              } else {
                arrayRemoveValue(expandedRowKeys, rowData[rowKey]);
              }
              // if `expandedRowKeys` is uncontrolled, update internal state
              if (!this.props.expandedRowKeys) {
                this.setState({ expandedRowKeys });
                this.recomputeRowHeights(rootIndex);
              }
              this.props.onRowExpand({ expanded, rowData, rowIndex });
              this.props.onExpandedRowsChange(expandedRowKeys);
            }}
          />
        )}
        {cell}
      </div>
    );
  }

  renderHeader({ key, columns, style }) {
    const { headerClassName, headerStyle, renderHeader } = this.props;

    const className = cn('BaseTable__header-row', headerClassName, {
      'BaseTable__header-row--resizing': !!this.state.resizingKey
    });
    const flattenedStyle = {
      ...style,
      ...headerStyle,
      height: this.props.headerHeight,
      overflow: 'hidden'
    };

    const props = {
      key,
      className,
      style: flattenedStyle,
      columns,
      renderCell: this.renderHeaderCell
    };

    return renderHeader ? renderHeader(props) : <TableHeader {...props} />;
  }

  renderHeaderCell({ column, columnIndex }) {
    const { headerClassName: className, title, renderHeader, key } = column;

    const cell = renderHeader ? (
      renderHeader({ column, columnIndex })
    ) : (
      <TableCell className="BaseTable__header-column-text" container={this._getContainerRef}>
        {title}
      </TableCell>
    );

    const { sort } = this.props;
    const sorting = column.key === sort.key;
    const sortOrder = sorting ? sort.order : SortOrder.ASC;
    const cls = cn('BaseTable__header-column', className, {
      'BaseTable__header-column--align-center': column.align === 'center',
      'BaseTable__header-column--align-right': column.align === 'right',
      'BaseTable__header-column--sortable': column.sortable,
      'BaseTable__header-column--sorting': sorting,
      'BaseTable__header-column--resizing': column.key === this.state.resizingKey
    });
    return (
      <div
        key={`Header-Col${columnIndex}`}
        role="button"
        data-testid={`BaseTable__column-header-${key}`}
        className={cls}
        style={this.columnManager.getColumnStyle(column.key)}
        onClick={column.sortable ? this._handleColumnSort(column) : null}
      >
        {column.key === this.props.expandColumnKey && <ExpandIcon />}
        {cell}
        {column.sortable && (
          <SvgIcon
            className={cn('BaseTable__sort-indicator', {
              'BaseTable__sort-indicator--descending': sortOrder === SortOrder.DESC
            })}
          >
            {tableArrow}
          </SvgIcon>
        )}
        {column.resizable !== false && (
          <ColumnResizer
            column={column}
            onResizeStart={this._handleColumnResizeStart}
            onResizeStop={this._handleColumnResizeStop}
            onResize={this._handleColumnResize}
          />
        )}
      </div>
    );
  }

  renderMainTable({ width, height }) {
    const { data, headerHeight, fixed, ...rest } = this.props;
    const { scrollbarWidth } = this.state;

    let tableWidth = width - scrollbarWidth;
    if (this.props.fixed) {
      const columnsWidth = this.columnManager.getColumnsWidth();
      tableWidth = Math.max(columnsWidth, tableWidth);
    }
    return (
      <GridTable
        {...rest}
        className="BaseTable__table-main"
        ref={this._setMainTableRef}
        data={data}
        frozenRowCount={this._getFrozenRowCount()}
        columns={this.columnManager.getMainColumns()}
        width={width}
        height={height}
        headerHeight={headerHeight}
        rowHeight={this._rowHeight}
        headerWidth={tableWidth + (fixed ? scrollbarWidth : 0)}
        bodyWidth={tableWidth}
        renderHeader={this.renderHeader}
        renderRow={this.renderRow}
        onScroll={this._handleScroll}
        onRowsRendered={this._handleRowsRendered}
        onScrollbarPresenceChange={this._handleScrollbarPresenceChange}
      />
    );
  }

  renderLeftTable({ width, height }) {
    if (!this.columnManager.hasLeftFrozenColumns()) return null;

    const { data, headerHeight, ...rest } = this.props;
    const { scrollbarWidth, horizontalScrollbarWidth } = this.state;

    const offset = scrollbarWidth || 15;
    const columnsWidth = this.columnManager.getLeftFrozenColumnsWidth();
    return (
      <GridTable
        {...rest}
        containerStyle={{
          width: columnsWidth,
          maxWidth: width,
          height: height - horizontalScrollbarWidth,
          overflow: 'hidden'
        }}
        className="BaseTable__table-frozen-left"
        ref={this._setLeftTableRef}
        data={data}
        frozenRowCount={this._getFrozenRowCount()}
        columns={this.columnManager.getLeftFrozenColumns()}
        width={columnsWidth + offset}
        height={height - horizontalScrollbarWidth}
        headerHeight={headerHeight}
        rowHeight={this._rowHeight}
        headerWidth={columnsWidth + offset}
        bodyWidth={columnsWidth + offset}
        renderHeader={this.renderHeader}
        renderRow={this.renderRow}
        onScroll={this._handleVerticalScroll}
        onRowsRendered={noop}
        onScrollbarPresenceChange={noop}
      />
    );
  }

  renderRightTable({ width, height }) {
    if (!this.columnManager.hasRightFrozenColumns()) return null;

    const { data, headerHeight, ...rest } = this.props;
    const { scrollbarWidth, horizontalScrollbarWidth } = this.state;

    const columnsWidth = this.columnManager.getRightFrozenColumnsWidth();
    return (
      <GridTable
        {...rest}
        containerStyle={{
          width: columnsWidth + scrollbarWidth,
          maxWidth: width,
          height: height - horizontalScrollbarWidth,
          overflow: 'hidden'
        }}
        className="BaseTable__table-frozen-right"
        ref={this._setRightTableRef}
        data={data}
        frozenRowCount={this._getFrozenRowCount()}
        columns={this.columnManager.getRightFrozenColumns()}
        width={columnsWidth + scrollbarWidth}
        height={height - horizontalScrollbarWidth}
        headerHeight={headerHeight}
        rowHeight={this._rowHeight}
        headerWidth={columnsWidth + scrollbarWidth}
        bodyWidth={columnsWidth}
        renderHeader={this.renderHeader}
        renderRow={this.renderRow}
        onScroll={this._handleVerticalScroll}
        onRowsRendered={noop}
        onScrollbarPresenceChange={noop}
      />
    );
  }

  renderFooter() {
    const { footerHeight, renderFooter } = this.props;
    if (footerHeight === 0) return null;
    return (
      <div className="BaseTable__footer" style={{ height: footerHeight }}>
        {renderFooter()}
      </div>
    );
  }

  renderDefaultEmptyLayer() {
    return (
      <div className="BaseTable__default-empty-layer">
        <SvgIcon width="70px" height="122px">
          {emptyBox}
        </SvgIcon>
        <span>
          {this.props.defaultEmptyLayerLabel ||
            this.props[TSymbolProp].translate('DEFAULT_EMPTY_LABEL')}
        </span>
      </div>
    );
  }

  renderEmptyLayer() {
    const renderEmpty = this.props.renderEmpty || this.renderDefaultEmptyLayer;

    return renderEmpty();
  }

  renderLoading() {
    return (
      <div className="BaseTable__loading-layer">
        <Loader />
      </div>
    );
  }

  renderLayer(layerContent) {
    const { headerHeight, footerHeight } = this.props;
    return (
      <div className="BaseTable__layer" style={{ top: headerHeight, bottom: footerHeight }}>
        {layerContent}
      </div>
    );
  }

  renderOverlays() {
    // This is an array of possible overlays,
    // Each overlay has a `show` attribute to determine whether or not we need to show it,
    // and a `render` attribute to actually render that overlay.
    // The order determines the overlay precedence.
    const overlays = [
      {
        show: this.props.showLoadingLayer,
        render: () => this.renderLoading()
      },
      {
        show: !(this.props.data && this.props.data.length),
        render: () => this.renderEmptyLayer()
      }
    ];

    const matchingOverlays = overlays.filter(overlay => overlay.show);

    return matchingOverlays.length > 0 ? this.renderLayer(matchingOverlays[0].render()) : null;
  }

  renderResizingLine({ width, height }) {
    const { resizingKey, scrollbarWidth } = this.state;
    if (!this.props.fixed || !resizingKey) return null;
    const columns = this.columnManager.getMainColumns();
    const idx = columns.findIndex(column => column.key === resizingKey);
    const column = this.columnManager.getColumn(resizingKey);

    let left = this.columnManager.recomputeColumnsWidth(columns.slice(0, idx + 1));
    if (!column.frozen) {
      left -= this._scroll.scrollLeft;
    } else if (column.frozen === 'right') {
      left =
        width - scrollbarWidth - this.columnManager.recomputeColumnsWidth(columns.slice(idx + 1));
    }
    const style = {
      left,
      width: 3,
      transform: 'translateX(-3px)',
      height: height - this.state.horizontalScrollbarWidth
    };
    return <div className="BaseTable__resizing-line" style={style} />;
  }

  renderTable({ width, height }) {
    const { disabled, className, style, footerHeight } = this.props;
    const cls = cn('BaseTable', className, {
      'BaseTable--has-frozen-rows': this._getFrozenRowCount() > 0,
      'BaseTable--has-frozen-columns': this.columnManager.hasFrozenColumns(),
      'BaseTable--disabled': disabled
    });
    const size = {
      width,
      height: height - footerHeight
    };
    return (
      <div ref={this._setContainerRef} className={cls} style={{ ...style, width, height }}>
        {this.renderFooter()}
        {this.renderMainTable(size)}
        {this.renderLeftTable(size)}
        {this.renderRightTable(size)}
        {this.renderOverlays()}
        {this.renderResizingLine(size)}
      </div>
    );
  }
  //endregion

  render() {
    const { width, maxHeight } = this.props;
    let { height } = this.props;
    if (maxHeight) {
      height = this.state.tableHeight;
    }
    return (
      <AutoResizer width={width} height={height} onResize={this._handleResize}>
        {size => this.renderTable(size)}
      </AutoResizer>
    );
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const state = {};
    let shouldForceUpdate = false;

    this.columnManager.reset(this.normalizeColumns(nextProps), nextProps.fixed);

    if (nextProps.data !== this.props.data) {
      this._lastScanedRowIndex = -1;
      this._hasDataChangedSinceEndReached = true;
    }

    // if the table is selectable
    if (nextProps.selectable) {
      // If `selectedRowKeys` is controlled
      if ('selectedRowKeys' in nextProps) {
        // In this case we just want to set the selected row keys,
        // even if they correspond to the data in the table or not, we don't care.
        // That's the consumer's responsibility to take care of that when using the controlled prop.
        if (nextProps.selectedRowKeys !== this.props.selectedRowKeys) {
          state.selectedRowKeys = [...nextProps.selectedRowKeys];
          shouldForceUpdate = true;
        }
      } else {
        // In this case, the single source of truth for the selection state is props.onSelectedRowsChanged.
        // That's why we need to check if the data has changed and filter out keys that don't exist anymore.
        // When the values actually do change, e.g. due to deleting rows or changing of selectable,
        // we call nextProps.onSelectedRowsChanged to let the consumer know the selection has changed.
        const selectableRowKeys = this.getSelectableRowKeys(nextProps.data, nextProps.selectable);
        // remove nonexistent keys from selectedRowKeys
        if (
          !arrayHasSameValues(selectableRowKeys, this.selectableRowKeys) &&
          this.state.selectedRowKeys.length > 0
        ) {
          const selectedRowKeys = this.state.selectedRowKeys.filter(key =>
            selectableRowKeys.includes(key)
          );
          if (!arrayHasSameValues(selectedRowKeys, this.state.selectedRowKeys)) {
            nextProps.onSelectedRowsChange([...selectedRowKeys]);
            state.selectedRowKeys = selectedRowKeys;
            shouldForceUpdate = true;
          }
        }
        this.selectableRowKeys = selectableRowKeys;
      }
    }
    // if the table is expandable
    if (nextProps.expandColumnKey) {
      if ('expandedRowKeys' in nextProps) {
        if (nextProps.expandedRowKeys !== this.props.expandedRowKeys) {
          state.expandedRowKeys = [...nextProps.expandedRowKeys];
          this.recomputeRowHeights(0);
          shouldForceUpdate = true;
        }
      } else {
        const expandableRowKeys = this.getExpandableRowKeys(nextProps.data);
        // remove nonexistent keys from expandedRowKeys
        if (
          !arrayHasSameValues(expandableRowKeys, this.expandableRowKeys) &&
          this.state.expandedRowKeys.length > 0
        ) {
          const expandedRowKeys = this.state.expandedRowKeys.filter(key =>
            expandableRowKeys.includes(key)
          );
          if (!arrayHasSameValues(expandedRowKeys, this.state.expandedRowKeys)) {
            nextProps.onExpandedRowsChange([...expandedRowKeys]);
            state.expandedRowKeys = expandedRowKeys;
            shouldForceUpdate = true;
          }
        }
        this.expandableRowKeys = expandableRowKeys;
      }

      const rowKeys = this.getAllRowKeys(nextProps.data);
      // recompute all the row heights after removed a row or sorted
      if (
        !arrayHasSameValues(rowKeys, this.rowKeys) ||
        nextProps.sort.key !== this.props.sort.key ||
        nextProps.sort.order !== this.props.sort.order
      ) {
        this.recomputeRowHeights(0);
      }
      this.rowKeys = rowKeys;
    }

    // we use custom rowHeight to support expandable table, so we have to recompute after rowHeight changed
    if (nextProps.rowHeight !== this.props.rowHeight) {
      this.recomputeRowHeights(0);
    }

    this.setState(state);
    if (shouldForceUpdate) this.forceUpdateTable();
  }

  componentDidMount() {
    this._maybeUpdateTableHeight();
  }

  componentDidUpdate(prevProps, prevState) {
    this._maybeUpdateTableHeight();
  }

  _maybeUpdateTableHeight() {
    const { maxHeight, headerHeight, footerHeight } = this.props;
    if (maxHeight) {
      const totalHeight = headerHeight + footerHeight + this.getTotalRowsHeight();
      const tableHeight = Math.min(totalHeight, maxHeight);
      if (tableHeight !== this.state.tableHeight) {
        this.setState({ tableHeight });
      }
    }
  }

  _getClientHeight() {
    if (this._scroll.clientHeight) return this._scroll.clientHeight;
    if (!this._size.height) return 0;
    return this._size.height - this.props.headerHeight - this.props.footerHeight;
  }

  _maybeCallOnEndReached() {
    const { data, maxHeight, onEndReached, onEndReachedThreshold, frozenRowCount } = this.props;
    const { scrollTop } = this._scroll;
    const scrollHeight = this.getTotalRowsHeight();
    const clientHeight = this._getClientHeight();
    const { horizontalScrollbarWidth } = this.state;
    // onEndReached is not available if maxHeight is set
    if (maxHeight || !onEndReached || !clientHeight || !scrollHeight) return;
    const distanceFromEnd = scrollHeight - scrollTop - clientHeight + horizontalScrollbarWidth;
    if (
      // don't call onEndReached if data is empty
      this._lastScanedRowIndex >= 0 &&
      this._lastScanedRowIndex === data.length - 1 - frozenRowCount &&
      distanceFromEnd <= onEndReachedThreshold &&
      (this._hasDataChangedSinceEndReached || scrollHeight !== this._scrollHeight)
    ) {
      // we set it to true when data changed(e.g. more data loaded), reset if when onEndReached is called
      this._hasDataChangedSinceEndReached = false;
      this._scrollHeight = scrollHeight;
      onEndReached({ distanceFromEnd });
    }
  }

  //region Handlers
  _handleScrollbarPresenceChange({ size, vertical, horizontal }) {
    this.setState({
      scrollbarWidth: vertical ? size : 0,
      horizontalScrollbarWidth: horizontal ? size : 0
    });
    this.props.onScrollbarPresenceChange({ size, vertical, horizontal });
  }

  _handleResize(size) {
    this.props.onResize(size);

    this._size = size;
    this._maybeCallOnEndReached();
  }

  _handleScroll(args) {
    const lastScrollTop = this._scroll.scrollTop || 0;
    this.scrollToPosition(args);
    this.props.onScroll(args);

    if (args.scrollTop > lastScrollTop) this._maybeCallOnEndReached();
  }

  _handleVerticalScroll({ scrollTop }) {
    const lastScrollTop = this._scroll.scrollTop || 0;
    this.scrollToTop(scrollTop);
    this.props.onScroll(this._scroll);

    if (scrollTop > lastScrollTop) this._maybeCallOnEndReached();
  }

  _handleRowsRendered(args) {
    this.props.onRowsRendered(args);

    if (args.overscanStopIndex > this._lastScanedRowIndex) {
      this._lastScanedRowIndex = args.overscanStopIndex;
      this._maybeCallOnEndReached();
    }
  }

  _handleColumnResize({ key }, width) {
    this.columnManager.setColumnWidth(key, width);
    this.forceUpdate();

    const column = this.columnManager.getColumn(key);
    this.props.onColumnResize({ column, width });
  }

  _handleColumnResizeStart({ key }) {
    this.setState({ resizingKey: key });
  }

  _handleColumnResizeStop() {
    this.setState({ resizingKey: null });
    this.forceUpdateTable();
  }

  _handleColumnSort(column) {
    return () => {
      const { sort, onColumnSort } = this.props;
      let order = SortOrder.ASC;
      if (column.key === sort.key) {
        order = sort.order === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
      }
      onColumnSort({
        key: column.key,
        order
      });
    };
  }

  _handleRowHover({ hovered, rowKey }) {
    this.setState({ hoverRowKey: hovered ? rowKey : null });
    this.forceUpdateTable();
  }
  //endregion
}

BaseTable.Column = Column;
BaseTable.SortOrder = SortOrder;

BaseTable.defaultProps = {
  rowKey: 'id',
  fixed: false,
  hideHeader: false,
  headerHeight: 50,
  rowHeight: 50,
  footerHeight: 0,
  defaultExpandedRowKeys: [],
  defaultSelectedRowKeys: [],
  sort: {},
  overscanRowCount: 10,
  frozenRowCount: 0,
  onEndReachedThreshold: 500,

  onScroll: noop,
  onRowsRendered: noop,
  onResize: noop,
  onScrollbarPresenceChange: noop,
  onRowSelect: noop,
  onSelectedRowsChange: noop,
  onSelectAll: noop,
  onRowExpand: noop,
  onExpandedRowsChange: noop,
  onColumnSort: noop,
  onColumnResize: noop,
  onRowAction: noop,
  renderFooter: noop,
  setContainerRef: noop
};

BaseTable.propTypes = {
  /**
   * Class name for the container
   */
  className: PropTypes.string,
  /**
   * Custom style for the container
   */
  style: PropTypes.object,
  /**
   * A collection of Column
   */
  children: PropTypes.node,
  /**
   * Columns for the table
   */
  columns: PropTypes.arrayOf(PropTypes.shape({ ...Column.propTypes })),
  /**
   * The data for the table
   */
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  /**
   * The count of rows be frozen to top
   */
  frozenRowCount: PropTypes.number,
  /**
   * The key field of each data item
   */
  rowKey: PropTypes.string.isRequired,
  /**
   * The width of the table, will be the container's width if not set
   */
  width: PropTypes.number,
  /**
   * The height of the table, will be the container's height if not set, will be ignored if `maxHeight` is set
   */
  height: PropTypes.number,
  /**
   * The max height of the table, the table's height will auto change when add/delete/expand rows,
   * will turns to vertical scroll if reaches the max height
   */
  maxHeight: PropTypes.number,
  /**
   * The height of each table row
   */
  rowHeight: PropTypes.number.isRequired,
  /**
   * The height of the table header
   */
  headerHeight: PropTypes.number.isRequired,
  /**
   * The height of the table footer
   */
  footerHeight: PropTypes.number,
  /**
   * Whether the width of the columns are fixed or flexible
   */
  fixed: PropTypes.bool,
  /**
   * Whether the table is disabled
   */
  disabled: PropTypes.bool,
  /**
   * Whether to show the table header
   */
  hideHeader: PropTypes.bool,
  /**
   * Custom props for the builtin selection column
   */
  selectionColumnProps: PropTypes.object,
  /**
   * Custom props for the builtin action column
   */
  actionColumnProps: PropTypes.object,
  /**
   * A callback funtion for the settings icon click event
   */
  onSettingsClick: PropTypes.func,
  /**
   * An array of ActionButton's options or a callback to return the options
   * The callback is of the shape of "({ rowData, rowIndex }) => array"
   */
  actionOptions: PropTypes.oneOfType([
    PropTypes.arrayOf(ActionButton.optionPropType),
    PropTypes.func
  ]),
  /**
   * A callback function for an action option click event
   * The handler is of the shape of "({ key, rowData, rowIndex }) => *"
   */
  onRowAction: PropTypes.func,
  /**
   * Optional renderer when the length of data is 0
   */
  renderEmpty: PropTypes.func,
  /**
   * Custom footer renderer, available only if `footerHeight` is larger then 0
   */
  renderFooter: PropTypes.func,
  /**
   * Custom header renderer
   * The callback is of the shape of "({ key, columns, style }) => *"
   */
  renderHeader: PropTypes.func,
  /**
   * Custom header renderer
   * The callback is of the shape of "({ key, columns, rowData, rowIndex, style, depth }) => *"
   */
  renderRow: PropTypes.func,
  /**
   * Class name for the table header
   */
  headerClassName: PropTypes.string,
  /**
   * Custom style for the table header
   */
  headerStyle: PropTypes.object,
  /**
   * Class name for the table row, could be a callback to return the class name
   * The callback is of the shape of "({ rowData, rowIndex }) => string"
   */
  rowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /**
   * Custom style for the table row, could be a callback to return the style
   * The callback is of the shape of "({ rowData, rowIndex }) => object"
   */
  rowStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /**
   * Whether the rows of the table are selectable, could be a callback to decide which row is selectable
   * The callback is of the shape of "({ rowData, rowIndex }) => bool"
   */
  selectable: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  /**
   * Default selected row keys when initalize the table
   */
  defaultSelectedRowKeys: PropTypes.arrayOf(PropTypes.string),
  /**
   * Controlled selected row keys
   */
  selectedRowKeys: PropTypes.arrayOf(PropTypes.string),
  /**
   * A callback function when select or unselect a row
   * The handler is of the shape of "({ selected, rowData, rowIndex }) => *"
   */
  onRowSelect: PropTypes.func,
  /**
   * A callback function when the selected row keys changed
   * The handler is of the shape of "(selectedRowKeys) => *"
   */
  onSelectedRowsChange: PropTypes.func,
  /**
   * A callback function when toggle the checkbox in the header of selection column
   * The handler is of the shape of "(selected, selectedRowKeys) => *"
   */
  onSelectAll: PropTypes.func,
  /**
   * The key for the expand column which render the expand icon if the data is a tree
   */
  expandColumnKey: PropTypes.string,
  /**
   * Default expanded row keys when initalize the table
   */
  defaultExpandedRowKeys: PropTypes.arrayOf(PropTypes.string),
  /**
   * Controlled expanded row keys
   */
  expandedRowKeys: PropTypes.arrayOf(PropTypes.string),
  /**
   * A callback function when expand or collapse a tree node
   * The handler is of the shape of "({ expanded, rowData, rowIndex }) => *"
   */
  onRowExpand: PropTypes.func,
  /**
   * A callback function when the expanded row keys changed
   * The handler is of the shape of "(expandedRowKeys) => *"
   */
  onExpandedRowsChange: PropTypes.func,
  /**
   * The sort state for the table
   */
  sort: PropTypes.shape({
    /**
     * Sort key
     */
    key: PropTypes.string,
    /**
     * Sort order
     */
    order: PropTypes.oneOf([SortOrder.ASC, SortOrder.DESC])
  }),
  /**
   * A callback function for the header cell click event
   * The handler is of the shape of "({ key, order }) => *"
   */
  onColumnSort: PropTypes.func,
  /**
   * A callback function when resizing the column width
   * The handler is of the shape of "({ column, width }) => *"
   */
  onColumnResize: PropTypes.func,
  /**
   * Number of rows to render above/below the visible bounds of the list
   */
  overscanRowCount: PropTypes.number,
  /**
   * A callback function when scrolling the table
   * The handler is of the shape of "({ scrollLeft, scrollTop }) => *"
   */
  onScroll: PropTypes.func,
  /**
   * A callback function when scrolling the table within `onEndReachedThreshold` of the bottom
   * The handler is of the shape of "({ distanceFromEnd }) => *"
   */
  onEndReached: PropTypes.func,
  /**
   * Threshold in pixels for calling `onEndReached`.
   */
  onEndReachedThreshold: PropTypes.number,
  /**
   * A callback function with information about the slice of rows that were just rendered
   * The handler is of the shape of "({ overscanStartIndex, overscanStopIndex, startIndex， stopIndex }) => *"
   */
  onRowsRendered: PropTypes.func,
  /**
   * A callback function when the size of the table container changed if the width and height are not set
   * The handler is of the shape of "({ width, height }) => *"
   */
  onResize: PropTypes.func,
  /**
   * A callback function when the scrollbar presence state changed
   * The handler is of the shape of "({ size, vertical, horizontal }) => *"
   */
  onScrollbarPresenceChange: PropTypes.func,
  /**
   * A object for the row event handlers
   * Each of the keys is row event name, like `onClick`, `onDoubleClick` and etc.
   * Each of the handlers is of the shape of "({ rowData, rowIndex, rowKey, event }) => *"
   */
  rowEventHandlers: PropTypes.object,
  /**
   * A callback function to set the ref of the container
   */
  setContainerRef: PropTypes.func,
  /**
   * Flag indicating whether to show a loading layer or not.
   */
  showLoadingLayer: PropTypes.bool,
  /**
   * The locale used to translate all the internal messages.
   */
  locale: PropTypes.string,
  /**
   * Set this prop when needing the default empty layer only with a different text.
   */
  defaultEmptyLayerLabel: PropTypes.string
};

export default withLocale(BaseTable, ({ locale }) => new MDText(require(`./i18n/${locale}.json`)));
